// Measurements dashboard widget (BODY_MEASUREMENTS_PLAN.md Task 6.1). Opt-in,
// off by default. Shows the last session's date and the three sites that
// moved the most since the prior session, deep-linking into the Measurements
// tab the same way LogsPane's body deep-link does (BodyPane.tsx reads
// `?tab=measurements` off the hash on mount).
import type { BodyMeasurementPayload } from "@medassist/shared";
import {
  DEFAULT_MEASUREMENT_UNIT,
  MEASUREMENT_SITE_BY_ID,
  buildMeasurementDeltas,
  formatMeasurement,
} from "@medassist/shared";
import { RulerIcon } from "../../icons.js";
import { relativeTime } from "../../../lib/time.js";
import type { WidgetProps } from "../types.js";

export function MeasurementsWidget({ patient, events }: WidgetProps) {
  const sessions = events.filter((e) => e.eventType === "body_measurement" && !e.deleted);
  const latest = sessions[0];
  const unit = patient.preferredMeasurementUnit ?? DEFAULT_MEASUREMENT_UNIT;

  const deltas = buildMeasurementDeltas(
    sessions.map((e) => e.payload as unknown as BodyMeasurementPayload)
  )
    .filter((d) => d.vsPreviousCm !== undefined)
    .sort((a, b) => Math.abs(b.vsPreviousCm!) - Math.abs(a.vsPreviousCm!))
    .slice(0, 3);

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <RulerIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Measurements</h2>
        <button
          onClick={() => { location.hash = "#/body?tab=measurements"; }}
          className="ml-auto text-xs text-(--accent) hover:underline"
        >
          Log measurements
        </button>
      </div>
      {latest ? (
        <>
          <p className="mb-2 text-xs text-gray-400 dark:text-gray-500">{relativeTime(latest.occurredAt)}</p>
          {deltas.length > 0 ? (
            <div className="space-y-1">
              {deltas.map((d) => (
                <div key={d.siteId} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    {MEASUREMENT_SITE_BY_ID[d.siteId]?.label ?? d.siteId}
                  </span>
                  <span className={d.vsPreviousCm! > 0 ? "font-medium text-amber-600 dark:text-amber-400" : "font-medium text-emerald-600 dark:text-emerald-400"}>
                    {d.vsPreviousCm! > 0 ? "+" : ""}
                    {formatMeasurement(d.vsPreviousCm!, unit)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No earlier session to compare yet</p>
          )}
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            {sessions.length} session{sessions.length === 1 ? "" : "s"}
          </p>
        </>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500">No measurement sessions yet</p>
      )}
    </section>
  );
}
